import _, {isEqual, isNull, keyBy, partition, without} from 'lodash';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {isWithinBounds, makeConversationBoundaryBuilder} from '../../../../helpers/cellsHelpers';
import {maybeBuildConversationOverlay} from '../../../../helpers/models/conversationOverlayHelpers';
import {collapseById} from '../../../../models/api/dataGroupModel';
import {AppAction} from '../../../appActions';
import {AsyncMoreStatusesEnum} from '../../../appTypes';
import {ConversationsActionsEnum} from '../../../data/conversations/conversationsActions';
import {DataCascade} from '../../../data/dataCascade';
import {DataGroupsActionsEnum} from '../../../data/dataGroupsActions';
import {InboxesParametersTypesEnum} from '../../inboxes/parameters/inboxesParameters';
import {
  CellsPanelConversationsActionsEnum,
  CellsPanelPageActionsEnum,
  isCellsPanelPageAction,
} from '../cellsPanelActions';
import {CellsPanelConversationsPageState} from '../cellsPanelState';
import {CellsContext} from '../context/cellsContext';
import {isConversationInPanel, shouldConversationBeHiddenFromPanel} from '../helpers/cellsPanelHelpers';
import {
  computeMoreStatusForBoundary,
  extractConversationMatchedQueries,
  extractConversationSearchResults,
  updateMoreStatus,
} from '../helpers/cellsPanelReducerHelpers';

export function cellsPanelConversationsReducer(
  state: CellsPanelConversationsPageState,
  action: AppAction,
  dataCascade: DataCascade,
): CellsPanelConversationsPageState {
  // If this is a cells page action, check that the uid matches.
  if (isCellsPanelPageAction(action) && action.payload.pageUid !== state.uid) {
    return state;
  }

  // Otherwise, proceed as usual.
  switch (action.type) {
    case CellsPanelPageActionsEnum.PAGE_NAVIGATED: {
      const {parameters} = action.payload;
      if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
        throw new Error('Incorrect parameters type.');
      }

      return {
        ...state,
        cellsContext: buildCellsContext(state.cellsContext, action.payload.cellsContext),
        parameters,
      };
    }

    case CellsPanelPageActionsEnum.REFETCH_STARTED:
      // If we are refetching a previously loaded page, only set the refetching flag for the mobile app.
      if (state.status === AsyncStatusesEnum.LOADED) {
        return {
          ...state,
          isRefetching: true,
        };
      }

      return {
        ...state,
        status: AsyncStatusesEnum.LOADING,
      };

    case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED: {
      const {payload} = action;
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        isLoadedFromCache: payload.isLoadedFromCache,
        isRefetching: false,
        moreStatusTop: computeMoreStatusForBoundary(payload.topBoundary),
        moreStatusBottom: computeMoreStatusForBoundary(payload.bottomBoundary),
        topBoundary: payload.topBoundary,
        bottomBoundary: payload.bottomBoundary,
        conversationIds: payload.ids,
        conversationHighlights: keyBy(payload.conversationHighlights, (h) => h.conversationId),
        conversationMatchedQueries: extractConversationMatchedQueries(payload.conversationMetas),
        conversationSearchResults: extractConversationSearchResults(payload.conversationSearchResults),
      };
    }

    case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_FAILED:
      if (state.status === AsyncStatusesEnum.LOADED && state.isLoadedFromCache) {
        return state;
      }

      return {
        ...state,
        status: AsyncStatusesEnum.FAILED,
        isRefetching: false,
        conversationIds: [],
      };

    case CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS:
      return {
        ...state,
        ...updateMoreStatus(action.payload.direction, AsyncMoreStatusesEnum.LOADING),
      };

    case CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_COMPLETED: {
      const {payload} = action;
      const conversationIds = _(state.conversationIds).concat(payload.ids).uniq().value();
      const conversationHighlights = {
        ...state.conversationHighlights,
        ...keyBy(payload.conversationHighlights, (h) => h.conversationId),
      };
      const conversationMatchedQueries = {
        ...state.conversationMatchedQueries,
        ...extractConversationMatchedQueries(payload.conversationMetas),
      };

      const conversationSearchResults = state.conversationSearchResults && {
        ...state.conversationSearchResults,
        ...extractConversationSearchResults(payload.conversationSearchResults),
      };

      if (payload.direction === ScrollDirectionsEnum.TOP) {
        return {
          ...state,
          moreStatusTop: computeMoreStatusForBoundary(payload.topBoundary),
          topBoundary: payload.topBoundary,
          conversationIds,
          conversationHighlights,
          conversationMatchedQueries,
          conversationSearchResults,
        };
      }

      return {
        ...state,
        moreStatusBottom: computeMoreStatusForBoundary(payload.bottomBoundary),
        bottomBoundary: payload.bottomBoundary,
        conversationIds,
        conversationHighlights,
        conversationMatchedQueries,
        conversationSearchResults,
      };
    }

    case CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_FAILED:
      return {
        ...state,
        ...updateMoreStatus(action.payload.direction, AsyncMoreStatusesEnum.ERROR),
      };

    case ConversationsActionsEnum.CLEAR_HIDDEN_CONVERSATION_ID: {
      if (isNull(state.hiddenConversationId)) {
        return state;
      }

      // If defined, hiddenConversationId will remain in removedConversationIds.
      return {
        ...state,
        conversationIds: without(state.conversationIds, state.hiddenConversationId),
        hiddenConversationId: null,
      };
    }

    // TODO: Can we decide this when the conversation state changes, rather than trying to predict it?
    case ConversationsActionsEnum.MAYBE_HIDE_CONVERSATION: {
      const {prevConversation, nextConversation} = action.payload;
      const dataState = dataCascade.data;

      const {parameters, cellsContext} = state;
      const isGoingToBeInPanel = isConversationInPanel(dataState, cellsContext, parameters, nextConversation);
      if (isGoingToBeInPanel) {
        return state;
      }

      const conversationId = nextConversation.id;
      const shouldBeHidden = shouldConversationBeHiddenFromPanel(
        parameters,
        cellsContext,
        nextConversation,
        prevConversation,
      );

      if (!shouldBeHidden) {
        return state;
      }

      return {
        ...state,
        hiddenConversationId: conversationId,
      };
    }

    case ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED:
    case ConversationsActionsEnum.CONVERSATION_DELETED: {
      // Conversation not currently visible: ignore.
      const {conversationIds} = state;
      const {conversationId} = action.payload;
      if (!conversationIds.includes(conversationId)) {
        return state;
      }

      return {
        ...state,
        conversationIds: without(conversationIds, conversationId),
      };
    }

    // When we receive the conversation content, it may no longer belong in the cells panel, but we don't want to hide it.
    case ConversationsActionsEnum.CONVERSATION_CONTENT_UPDATED:
      return state;

    default: {
      const {
        shouldNotCascade = false,
        isComingFromPusher = false,
        requesterId = undefined,
        isComingFromViewerFetch = false,
      } = action.type === DataGroupsActionsEnum.DATA_UPDATED ? action.payload : {};

      if (shouldNotCascade || isComingFromViewerFetch) {
        return state;
      }

      const {data, prevData} = dataCascade;
      const {parameters, cellsContext, conversationIds, removedConversationIds} = state;

      const updatedConversations = dataCascade.updatedResources.conversations;
      if (!updatedConversations || updatedConversations.length === 0 || parameters.search) {
        return state;
      }

      // Find which of the updated conversations should appear or not in the list.
      const boundaryBuilder = makeConversationBoundaryBuilder(cellsContext, parameters);
      const [shouldAppear, shouldNotAppear] = partition(collapseById(updatedConversations), (c) => {
        if (!isConversationInPanel(data, cellsContext, parameters, c)) {
          return false;
        }

        const boundary = boundaryBuilder(c);
        return isWithinBounds(state, parameters.sortDirection, boundary);
      });

      // Find what to update.
      const shouldBeAdded = _(shouldAppear)
        .map((c) => c.id)
        .difference(conversationIds)
        .value();
      const shouldBeRemoved = _(shouldNotAppear)
        .map((c) => c.id)
        .intersection(conversationIds)
        .value();

      const conversationOverlaysPatch = _(shouldBeRemoved)
        .map((conversationId) => {
          const existingConversation = prevData?.conversations.byId[conversationId];
          const newConversation = data.conversations.byId[conversationId];

          if (!isComingFromPusher) {
            return undefined;
          }

          if (!newConversation) {
            return undefined;
          }

          return maybeBuildConversationOverlay({
            cellsContext,
            newConversation,
            existingConversation,
            requesterId,
          });
        })
        .compact()
        .keyBy((overlay) => overlay.conversationId)
        .value();

      const newConversationOverlays = {
        ...state.conversationOverlays,
        ...conversationOverlaysPatch,
      };

      // We may not need to do anything.
      if (shouldBeAdded.length === 0 && shouldBeRemoved.length === 0) {
        return state;
      }

      // Otherwise, find the new conversation IDs.
      const newConversationIds = _(conversationIds)
        .concat(shouldBeAdded)
        .difference(shouldBeRemoved)
        .concat((state.hiddenConversationId && [state.hiddenConversationId]) || [])
        .compact()
        .uniq()
        .value();

      // Keep track of the conversations that were removed.
      const newRemovedConversationIds = _(removedConversationIds).concat(shouldBeRemoved).uniq().value();

      // Update the state with the new list.
      return {
        ...state,
        conversationIds: newConversationIds,
        removedConversationIds: newRemovedConversationIds,
        conversationOverlays: newConversationOverlays,
      };
    }
  }
}

/*
 * Helpers.
 */

function buildCellsContext(existingCellsContext: CellsContext, updatedCellsContext: CellsContext) {
  if (isEqual(updatedCellsContext, existingCellsContext)) {
    // Prevent an identity change.
    return existingCellsContext;
  }

  return updatedCellsContext;
}
